export function assembleGiftItems({ fields, extras, title }) {
  const values = new Map(fields);
  const id = Number(values.get('id'));
  const quantity = Number(values.get('quantity') || 1);
  if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) throw new Error('Invalid flower selection');
  const properties = {};
  for (const [name, value] of fields) {
    const match = name.match(/^properties\[(.+)\]$/);
    if (match && String(value).trim()) properties[match[1]] = String(value);
  }
  const item = { id, quantity, properties };
  if (values.get('selling_plan')) item.selling_plan = values.get('selling_plan');
  const ids = [...new Set(extras.map(Number))];
  if (ids.some(extra => !Number.isSafeInteger(extra) || extra <= 0 || extra === id)) throw new Error('Invalid extra selection');
  return [item, ...ids.map(extra => ({ id: extra, quantity: 1, properties: { 'For flowers': title } }))];
}

export function sendGiftItems(root, items, errorMessage) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', root + 'cart/add.js');
    request.setRequestHeader('Content-Type', 'application/json');
    request.setRequestHeader('Accept', 'application/json');
    request.onload = () => {
      let result;
      try { result = JSON.parse(request.responseText); }
      catch { reject(new Error(errorMessage)); return; }
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(result.description || result.message || errorMessage));
        return;
      }
      resolve(result);
    };
    request.onerror = () => reject(new Error(errorMessage));
    request.onabort = () => reject(new Error(errorMessage));
    request.send(JSON.stringify({ items }));
  });
}

function bindGift(panel) {
  if (panel.dataset.bound) return;
  const details = panel.closest('.product-details') || panel.closest('.product-main');
  const form = details?.querySelector('form[data-product-form]');
  if (!form) return;
  details.closest('.product--section')?.classList.add('nl-occasion-product');
  const variants = JSON.parse(panel.querySelector('[data-nl-gift-variants]').textContent);
  const text = JSON.parse(panel.querySelector('[data-nl-gift-text]').textContent);
  panel.dataset.bound = 'true';
  form.dataset.nlGiftForm = 'true';
  const status = panel.querySelector('[data-nl-gift-status]');
  const button = form.querySelector('[data-product-atc]');
  const selection = details.querySelector('variant-selection');
  const money = value => new Intl.NumberFormat('en-IE', { style: 'currency', currency: panel.dataset.currency || 'EUR' }).format(value / 100);
  const dateField = () => details.querySelector('#datepickerapp input.flatpickr-input:not([type="hidden"])') || details.querySelector('#datepickerapp .form-control:not([type="hidden"])');
  const currentId = () => selection?.getAttribute('variant') || form.querySelector('[name="id"]')?.value;
  const selectedExtras = () => [...panel.querySelectorAll('[data-nl-gift-extra]:checked')].filter(input => !input.disabled);
  let busy = false;
  let scheduled = false;
  function refresh() {
    const variant = variants[currentId()];
    const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
    const price = (variant?.price || 0) * quantity + selectedExtras().reduce((sum, select) => sum + Number(select.dataset.price || 0), 0);
    const total = panel.querySelector('[data-nl-gift-total]');
    const formattedPrice = money(price);
    if (total.textContent !== formattedPrice) total.textContent = formattedPrice;
    const hasDate = !!dateField()?.value.trim();
    const disabled = busy || !variant?.available || !hasDate;
    if (button.disabled !== disabled) button.disabled = disabled;
    button.classList.toggle('processing', busy);
    if (busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
    const dateError = form.querySelector('[data-nlfq-date-error]');
    if (dateError) dateError.hidden = hasDate;
  }
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; refresh(); });
  };
  details.addEventListener('input', schedule);
  details.addEventListener('change', schedule);
  details.addEventListener('click', schedule);
  if (selection) new MutationObserver(schedule).observe(selection, { attributes: true, attributeFilter: ['variant'] });
  new MutationObserver(schedule).observe(details, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'value'] });
  panel.nlGiftSubmit = async event => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    refresh();
    if (!variants[currentId()]?.available) { status.textContent = text.variantRequired; return; }
    if (!dateField()?.value.trim()) { status.textContent = text.dateRequired; dateField()?.focus(); return; }
    if (!form.reportValidity()) return;
    const fields = [...new FormData(form).entries()];
    details.querySelectorAll('#datepickerapp [name^="properties["]').forEach(input => { if (input.value) fields.push([input.name, input.value]); });
    const message = details.querySelector('[data-florist-message]');
    if (message) fields.push(['properties[Message]', message.value]);
    fields.push(['id', currentId()]);
    busy = true;
    status.textContent = text.adding;
    refresh();
    try {
      const items = assembleGiftItems({ fields, extras: selectedExtras().map(select => select.value), title: panel.dataset.productTitle });
      const root = panel.dataset.root || '/';
      await sendGiftItems(root, items, text.error);
      status.textContent = text.added;
      window.location.assign(root + 'cart');
    } catch (error) {
      status.textContent = error.message || text.error;
      busy = false;
      refresh();
    }
  };
  refresh();
}

if (typeof window !== 'undefined') {
  window.addEventListener('submit', event => {
    if (!event.target.matches?.('form[data-nl-gift-form]')) return;
    const details = event.target.closest('.product-details') || event.target.closest('.product-main');
    details?.querySelector('[data-nl-gift-extras]')?.nlGiftSubmit?.(event);
  }, true);
  const start = () => document.querySelectorAll('[data-nl-gift-extras]').forEach(bindGift);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
}
